import type { Event, ExhibitionEvent, Thing, WithContext } from 'schema-dts';
import type { Question } from '@/entities/question';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { generateFaqPageJsonLd } from '@/shared/lib/utils/faq-json-ld';
import {
    type SchemaContacts,
    buildSchemaPlace,
    generateBreadcrumbListJsonLd,
    oneLine,
    schemaEventImage,
    schemaIdRef,
    schemaTextWithoutExpo,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import {
    SCHEMA_EVENT_ATTENDANCE,
    SCHEMA_EVENT_STATUS,
    SCHEMA_EXPO_FALLBACK_DESCRIPTION,
    SCHEMA_EXPO_FALLBACK_NAME,
    getSchemaIds,
    schemaAbsoluteUrl,
} from '@/shared/lib/utils/schema-ids';

export function generateExpoJsonLd(input: {
    metaDescription?: string | null;
    h1: string;
    draftsDescriptionHtml?: string | null;
    imageSrc?: string | null;
    eventStartDateIso?: string | null;
    eventEndDateIso?: string | null;
    breadcrumbs: AppBreadcrumbs;
    faqQuestions: Question[];
    contacts?: SchemaContacts | null;
}): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaAbsoluteUrl('/expo/');
    const eventImage = schemaEventImage(input.imageSrc);
    const disambiguatingText = input.draftsDescriptionHtml ? oneLine(stripHtml(input.draftsDescriptionHtml)) : '';

    const event: WithContext<ExhibitionEvent> = {
        '@context': 'https://schema.org',
        '@type': 'ExhibitionEvent',
        '@id': ids.exhibition,
        name: schemaTextWithoutExpo(input.h1, SCHEMA_EXPO_FALLBACK_NAME),
        url: pageUrl,
        description: schemaTextWithoutExpo(input.metaDescription, SCHEMA_EXPO_FALLBACK_DESCRIPTION),
        ...(disambiguatingText ? { disambiguatingDescription: disambiguatingText } : {}),
        ...(input.eventStartDateIso ? { startDate: input.eventStartDateIso } : {}),
        ...(input.eventEndDateIso ? { endDate: input.eventEndDateIso } : {}),
        inLanguage: 'ru',
        eventStatus: SCHEMA_EVENT_STATUS,
        eventAttendanceMode: SCHEMA_EVENT_ATTENDANCE,
        location: buildSchemaPlace(input.contacts),
        organizer: schemaIdRef(ids.organization) as Event['organizer'],
        superEvent: schemaIdRef(ids.tnfExpo) as Event['superEvent'],
        ...(eventImage ? { image: eventImage } : {}),
    };

    const graph: WithContext<Thing>[] = [
        event as WithContext<Thing>,
        generateBreadcrumbListJsonLd(input.breadcrumbs) as WithContext<Thing>,
    ];

    const faq = generateFaqPageJsonLd(input.faqQuestions, pageUrl);
    if (faq) graph.push(faq);

    return graph;
}
